<script setup>
import { LoaderCircleIcon } from "lucide-vue-next";
import { computed, ref } from "vue";

import { Link } from "@inertiajs/vue3";

const emit = defineEmits(["created"]);

const props = defineProps({
  campaign: {
    type: Object,
    required: true
  },
  page: {
    type: Object,
    required: true
  },
  parentComment: {
    type: Object,
    required: false
  },
  isLoggedIn: {
    type: Boolean,
    required: true
  },
  isProcessing: {
    type: Boolean,
    required: false,
    default: false
  },
  placeholder: {
    type: String,
    required: false
  },
  submitText: {
    type: String,
    required: false,
    default: "댓글 작성"
  },
  maxlength: {
    type: Number,
    required: false,
    default: 512
  },
  onCreate: {
    type: Function,
    required: false
  }
});

const comment = ref("");

const hasCommentText = computed(() => comment.value?.trim()?.length > 0);

const submitComment = () => {
  if (props.onCreate) {
    props.onCreate(comment.value, props.parentComment).then((newComment) => {
      emit("created", newComment);
    });
  }

  comment.value = "";
};

const handleEnterSubmit = (event) => {
  if (event.shiftKey || event.ctrlKey) {
    submitComment();
  }
};
</script>

<template>
  <div class="flex-none gap-x-3 border-t border-zinc-300 p-2">
    <div v-if="isLoggedIn" class="relative flex-auto text-sm+">
      <div class="overflow-hidden rounded-xl">
        <label for="comment" class="sr-only">comment</label>
        <textarea
          id="comment"
          v-model="comment"
          rows="2"
          class="block w-full resize-none border-0 border-zinc-200 bg-transparent pr-32 text-sm+ text-zinc-900 placeholder:text-zinc-400 focus:ring-0"
          :disabled="isProcessing"
          :placeholder="placeholder || '댓글을 입력해주세요.'"
          :minlength="1"
          :maxlength="maxlength"
          @keyup.enter.prevent="handleEnterSubmit"
        />
      </div>

      <div class="absolute right-2 top-2">
        <button
          type="submit"
          class="relative rounded-md bg-[#fff] px-4 py-2 font-bold text-zinc-900 shadow-sm ring-1 ring-inset ring-zinc-300 hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50"
          :disabled="isProcessing || !hasCommentText"
          @click="submitComment"
        >
          <span :class="{ 'opacity-0': isProcessing }">{{ submitText }}</span>
          <span v-if="isProcessing" class="absolute inset-0 flex items-center justify-center">
            <LoaderCircleIcon class="h-5 w-5 animate-spin" />
          </span>
        </button>
      </div>
    </div>
    <div v-else class="flex-center h-full py-4 text-sm font-semibold">
      <Link
        :href="route('login') + '?redirect-to=/' + campaign.team.name + '/p/' + page.hashid"
        class="text-primary opacity-50 transition hover:opacity-100"
      >
        <u>로그인</u> 후 댓글을 작성할 수 있습니다.
      </Link>
    </div>
  </div>
</template>
